import type { Prisma } from '@prisma/client';

import { specialtyReasons } from '~/config/referralReasons';
import { getSpecialtyConfig } from '~/config/specialties';
import type { DentalSpecialty, SpecialtyFormConfig } from '~/types/specialty';
import {
  cleanNotesForDisplay,
  formatDateValue,
  getBooleanQuestionText,
  getDateFieldLabelLocal,
  getDateValues,
} from '~/utils/formatters';
import { formatToothOrAreaWithInitials, isPediatricTooth } from '~/utils/tooth-helpers';

export function renderReasonGroupsToLines(referral: any): string[] {
  if (!referral.reasonGroups || referral.reasonGroups.length === 0) {
    return ['No specific reasons provided'];
  }

  const specialtyConfig = getSpecialtyConfig(referral.specialty);

  return referral.reasonGroups.flatMap((group: any, index: number) =>
    renderReasonGroupAsLines(group, index, specialtyConfig, referral.specialty)
  );
}

function renderReasonGroupAsLines(
  group: any,
  index: number,
  specialtyConfig: SpecialtyFormConfig | undefined,
  specialty: string | undefined
): string[] {
  const lines: string[] = [];

  lines.push(
    `${index + 1}. ${formatSpecialtyReason(
      specialtyConfig?.specialty as DentalSpecialty,
      group.reasonCode,
      {
        storedLabel: group.reasonLabel,
      }
    )}`
  );
  lines.push(`   ${formatSelectedTeeth(group)}`);

  if (group.diagnosis?.length) {
    lines.push(`   ${group.diagnosis.join(', ')}`);
  }

  if (group.selectedProceduresByGroup && typeof group.selectedProceduresByGroup === 'object') {
    for (const [groupName, rawValue] of Object.entries(
      group.selectedProceduresByGroup as Record<string, unknown>
    )) {
      const values = (Array.isArray(rawValue) ? rawValue : [rawValue]).filter(
        (value): value is string => typeof value === 'string' && value.length > 0
      );
      if (values.length > 0) {
        lines.push(
          `   - ${groupName}: ${values
            .map(value => resolveProcedureLabel(specialty, groupName, value))
            .join(', ')}`
        );
      }
    }
  }

  if (group.booleanAnswers && Object.keys(group.booleanAnswers).length > 0) {
    for (const [key, value] of Object.entries(group.booleanAnswers)) {
      lines.push(`   - ${getBooleanQuestionText(key, specialtyConfig)}: ${value ? 'Yes' : 'No'}`);
    }
  }

  if (group.dateValues) {
    const dates = getDateValues(group.dateValues);
    for (const [key, value] of Object.entries(dates)) {
      const dateStr = value === null ? 'N/A' : formatDateValue(String(value));
      lines.push(`   - ${getDateFieldLabelLocal(key, specialtyConfig)}: ${dateStr}`);
    }
  }

  if (group.notes) {
    lines.push(`   ${cleanNotesForDisplay(group.notes)}`);
  }

  return lines;
}

function formatSelectedTeeth(group: any): string {
  if (!group.selectedTeeth || group.selectedTeeth.length === 0) {
    return '';
  }

  const adultTeeth = group.selectedTeeth
    .filter((tooth: number) => !isPediatricTooth(tooth))
    .sort()
    .map(formatToothOrAreaWithInitials);

  const pediatricTeeth = group.selectedTeeth
    .filter((tooth: number) => isPediatricTooth(tooth))
    .sort()
    .map(formatToothOrAreaWithInitials);

  const parts: string[] = [];
  if (adultTeeth.length) parts.push(adultTeeth.join(', '));
  if (pediatricTeeth.length) parts.push(pediatricTeeth.join(', '));

  return parts.join('; ');
}

/** Strip the base36 uniqueness suffix from office custom reason codes (custom_<slug>_<id>). */
export function formatCustomReasonCode(reasonCode: string): string | null {
  if (!reasonCode.startsWith('custom_')) return null;
  const rest = reasonCode.slice('custom_'.length);
  const match = rest.match(/^(.+)_([a-z0-9]{5,12})$/i);
  if (!match) return null;
  return match[1]
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export const formatReason = (reasonCode: string) => {
  const customLabel = formatCustomReasonCode(reasonCode);
  if (customLabel) return customLabel;

  // If it's a custom reason (not from predefined list), return as is
  if (!reasonCode.includes('_')) return reasonCode.charAt(0).toUpperCase() + reasonCode.slice(1);

  // Convert snake_case to Title Case
  return reasonCode
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Resolves a selectedProceduresByGroup value to its configured procedure option
 * label (e.g. "head_neck_lesion" -> "Head / Neck Lesion"), mirroring the lookup
 * in `formatSpecialtyConfigProceduresColon` (app/utils/formatters.ts). Falls back
 * to `formatReason` when the specialty, procedure group, or option isn't found —
 * this is also the path taken by specialty-level treatment-question values
 * (e.g. "Temporary Restoration"), which aren't part of `specialtyReasons`
 * procedures and so pass through unchanged.
 */
function resolveProcedureLabel(
  specialty: string | undefined,
  groupName: string,
  value: string
): string {
  const reasons = specialty ? specialtyReasons[specialty] : undefined;
  const procedureGroup = reasons
    ?.flatMap(reason => reason.procedures ?? [])
    .find(group => group.name === groupName);

  const option = procedureGroup?.options.find(opt => opt.value === value);

  return option?.label ?? formatReason(value);
}

export const formatSpecialtyReason = (
  specialty: DentalSpecialty,
  reasonCode: string,
  options?: { storedLabel?: string | null; officeReasonLabel?: string | null }
) => {
  if (options?.storedLabel?.trim()) return options.storedLabel.trim();
  if (options?.officeReasonLabel?.trim()) return options.officeReasonLabel.trim();

  const reasons = specialtyReasons[specialty];
  if (!reasons) return formatReason(reasonCode);

  const formatted = reasons.find(r => r.value === reasonCode)?.label;

  return formatted || formatReason(reasonCode);
};

export async function canUserChangeReferralStatus(
  referralId: string,
  userId: string,
  tx: Prisma.TransactionClient
): Promise<boolean> {
  const result = await tx.$queryRawUnsafe<Array<{ allowed: boolean }>>(
    `
    SELECT EXISTS (
      SELECT 1
      FROM "Referral" r
      JOIN "DoctorProfile" receiving_doctor ON receiving_doctor.id = r."receivingDoctorId"
      JOIN "DoctorProfile" current_doctor ON current_doctor.id = $2
      WHERE r.id = $1
        AND (
          current_doctor.id = r."receivingDoctorId"
          OR current_doctor."officeId" = receiving_doctor."officeId"
        )
    ) AS "allowed"
    `,
    referralId,
    userId
  );

  return result?.[0]?.allowed ?? false;
}

export async function canUserEditReferral(
  referralId: string,
  userId: string,
  tx: Prisma.TransactionClient
): Promise<boolean> {
  const result = await tx.$queryRawUnsafe<Array<{ allowed: boolean }>>(
    `
    SELECT EXISTS (
      SELECT 1
      FROM "Referral" r
      JOIN "DoctorProfile" referring_doctor ON referring_doctor.id = r."referringDoctorId"
      JOIN "DoctorProfile" receiving_doctor ON receiving_doctor.id = r."receivingDoctorId"
      JOIN "DoctorProfile" current_doctor ON current_doctor.id = $2
      WHERE r.id = $1
        AND (
          current_doctor.id = r."referringDoctorId"
          OR current_doctor."officeId" = referring_doctor."officeId"
          OR current_doctor.id = r."receivingDoctorId"
          OR current_doctor."officeId" = receiving_doctor."officeId"
        )
    ) AS "allowed"
    `,
    referralId,
    userId
  );

  return result?.[0]?.allowed ?? false;
}

/**
 * Checks if the user can send a completion note for a referral.
 * Only users from the receiving party (receiving doctor or same office/company) can send completion notes.
 * @param referralId - The ID of the referral
 * @param userId - The ID of the user attempting to send the completion note
 * @param tx - Transaction-scoped Prisma client
 * @returns true if the user can send a completion note, false otherwise
 */
export async function canUserSendCompletionNote(
  referralId: string,
  userId: string,
  tx: Prisma.TransactionClient
): Promise<boolean> {
  const result = await tx.$queryRawUnsafe<Array<{ allowed: boolean }>>(
    `
    SELECT EXISTS (
      SELECT 1
      FROM "Referral" r
      JOIN "DoctorProfile" receiving_doctor ON receiving_doctor.id = r."receivingDoctorId"
      JOIN "DoctorProfile" current_doctor ON current_doctor.id = $2
      WHERE r.id = $1
        AND (
          current_doctor.id = r."receivingDoctorId"
          OR current_doctor."officeId" = receiving_doctor."officeId"
          OR (
            current_doctor."companyId" IS NOT NULL
            AND receiving_doctor."companyId" IS NOT NULL
            AND current_doctor."companyId" = receiving_doctor."companyId"
          )
        )
    ) AS "allowed"
    `,
    referralId,
    userId
  );

  return result?.[0]?.allowed ?? false;
}

export async function canUserDeleteReferral(
  referralId: string,
  userId: string,
  tx: Prisma.TransactionClient
): Promise<boolean> {
  const result = await tx.$queryRawUnsafe<Array<{ allowed: boolean }>>(
    `
    SELECT EXISTS (
      SELECT 1
      FROM "Referral" r
      JOIN "DoctorProfile" current_doctor ON current_doctor.id = $2
      JOIN "DoctorProfile" referring_doctor ON referring_doctor.id = r."referringDoctorId"
      JOIN "DoctorProfile" receiving_doctor ON receiving_doctor.id = r."receivingDoctorId"
      WHERE r.id = $1
        AND r."deletedAt" IS NULL
        AND (
          (
            r."createdAt" > (NOW() - INTERVAL '15 minutes')
            AND (
              current_doctor.id = r."referringDoctorId"
              OR (
                current_doctor."officeId" IS NOT NULL
                AND referring_doctor."officeId" IS NOT NULL
                AND current_doctor."officeId" = referring_doctor."officeId"
              )
              OR (
                current_doctor."companyId" IS NOT NULL
                AND referring_doctor."companyId" IS NOT NULL
                AND current_doctor."companyId" = referring_doctor."companyId"
              )
            )
          )
          OR
          (
            r."createdAt" <= (NOW() - INTERVAL '15 minutes')
            AND (
              current_doctor.id = r."receivingDoctorId"
              OR (
                current_doctor."officeId" IS NOT NULL
                AND receiving_doctor."officeId" IS NOT NULL
                AND current_doctor."officeId" = receiving_doctor."officeId"
              )
              OR (
                current_doctor."companyId" IS NOT NULL
                AND receiving_doctor."companyId" IS NOT NULL
                AND current_doctor."companyId" = receiving_doctor."companyId"
              )
            )
          )
        )
    ) AS "allowed"
    `,
    referralId,
    userId
  );

  return result?.[0]?.allowed ?? false;
}

export function getReferralDeleteWindowState(createdAt: Date | string) {
  const created = createdAt instanceof Date ? createdAt : new Date(createdAt);
  const deleteWindowEndsAt = new Date(created.getTime() + 15 * 60 * 1000);
  const now = new Date();
  const isSenderWindow = now < deleteWindowEndsAt;

  return {
    isSenderWindow,
    deleteWindowEndsAt,
  };
}

/**
 * Determines which company should receive a notification or email
 * The target is the company that is NOT making the update
 * @param referral - The referral object with referring and receiving doctor information
 * @param currentUserCompanyId - The company ID of the user making the update
 * @returns The company ID that should receive the notification, or null if no target found
 */
export function getTargetCompanyIdFromReferral(
  referral: {
    referringDoctor: { companyId: string | null };
    receivingDoctor: { companyId: string | null };
  },
  currentUserCompanyId: string | null
): string | null {
  if (!currentUserCompanyId) {
    return null;
  }

  // If the referring doctor's company is making the update, notify the receiving doctor's company
  if (referral.referringDoctor.companyId === currentUserCompanyId) {
    return referral.receivingDoctor.companyId;
  }

  // If the receiving doctor's company is making the update, notify the referring doctor's company
  if (referral.receivingDoctor.companyId === currentUserCompanyId) {
    return referral.referringDoctor.companyId;
  }

  return null;
}
